import type {
  CardStub,
  DashboardSection,
} from '@/features/dashboard/api/schemas'
import { isKpi, isTableStub } from '@/features/dashboard/model/sections'

/**
 * How many of `divisions` equal grid tracks each card spans. A card with `layout.blocks` spans that many; one without
 * fills what is left of its row (or a whole row when the row is full), so a
 * compact card beside a filler is just a card with `blocks` and one without. A
 * span never exceeds the divisions.
 */
export const computeBlockSpans = (
  cards: CardStub[],
  divisions: number
): number[] => {
  let rowUsed = 0
  return cards.map(card => {
    const explicit = card.layout?.blocks
    if (explicit) {
      const span = Math.min(explicit, divisions)
      rowUsed = (rowUsed + span) % divisions
      return span
    }
    const remaining = divisions - rowUsed
    rowUsed = 0
    return remaining > 0 ? remaining : divisions
  })
}

/**
 * - `blocks`: laid out on `divisions` equal tracks, each card spanning its blocks;
 * - `grid`: a headed group, KPI and chart cards inline, in order, on the auto-fill grid;
 * - `flat`: no heading or divisions, the KPI cards hoisted into a row above the charts.
 */
export type DeepDiveGroup = {
  key: string
  /** Blank when the group has none. */
  heading: string
  layout: 'blocks' | 'grid' | 'flat'
  divisions: number
  cards: CardStub[]
}

/**
 * The groups of a deep dive, in order: its subsections, or the deep dive's
 * cards as one flat group where it has none.
 */
export const deepDiveGroups = (section: DashboardSection): DeepDiveGroup[] => {
  const subsections = section.subsections ?? []
  if (subsections.length === 0) {
    return section.cards.length === 0
      ? []
      : [
          {
            key: section.id,
            heading: '',
            layout: 'flat',
            divisions: 0,
            cards: section.cards,
          },
        ]
  }
  return subsections
    .filter(subsection => subsection.cards.length > 0)
    .map(subsection => {
      const heading = subsection.title.trim()
      const divisions = subsection.divisions ?? 0
      return {
        key: subsection.id,
        heading,
        layout: divisions > 0 ? 'blocks' : heading ? 'grid' : 'flat',
        divisions,
        cards: subsection.cards,
      }
    })
}

/** The group's cards that are KPIs, then those that are not. */
export const splitKpis = (cards: CardStub[]) => ({
  kpis: cards.filter(isKpi),
  charts: cards.filter(card => !isKpi(card)),
})

/**
 * The groups of a deep dive without the card the page opens with (its home
 * card), and any group that leaves empty.
 */
export const groupsWithout = (
  section: DashboardSection,
  cardId: number | undefined
): DeepDiveGroup[] =>
  deepDiveGroups(section)
    .map(group => ({
      ...group,
      cards: group.cards.filter(stub => stub.cardId !== cardId),
    }))
    .filter(group => group.cards.length > 0)

/** Whether the page has a chart to list in a table: not a KPI, a table or a pending tile. */
export const hasChartCards = (
  groups: DeepDiveGroup[],
  home: { stub: CardStub } | undefined
) =>
  (home !== undefined && !isKpi(home.stub)) ||
  groups.some(group =>
    group.cards.some(
      stub => !isKpi(stub) && !isTableStub(stub) && !stub.pending
    )
  )
